import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Users,
  Camera,
  Upload,
  Edit,
  Eye,
  Trash2,
  Download,
  RefreshCw,
  Loader2,
  FileSpreadsheet,
  FileType,
  Save,
  Pen,
  X,
  ScanLine,
} from "lucide-react";
import { Button } from "../ui/button";
import { catastroUrl } from "../../lib/catastroLink";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import DocumentosAdicionales from "../DocumentosAdicionalesRes022";
import EtiquetaNavarra from "./EtiquetaNavarra";
import JustificanteSection from "../JustificanteSectionRes022";
import GreenflexSectionRes022 from "./GreenflexSectionRes022";
import EnviarDocumentosSection from "./EnviarDocumentosSection";
import ControlDossierRes022 from "./ControlDossierRes022";
import { calcularBeneficio, redondeoAE } from "../../lib/beneficio";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const API_DOCS = `${BACKEND_URL}/api/docs-v3`;

// StatusBadge - copie exacte de App.js ligne 370
const StatusBadge = ({ status }) => {
  const statusConfig = {
    nuevo: { label: "Nuevo", className: "bg-blue-100 text-blue-800" },
    en_cours: { label: "En Curso", className: "bg-orange-100 text-orange-800" },
    enviado_greenflex: { label: "Enviado Greenflex", className: "bg-emerald-100 text-emerald-800" },
    enviado_moneleg: { label: "Enviado Moneleg", className: "bg-violet-100 text-violet-800" },
  };
  const config = statusConfig[status] || statusConfig.nuevo;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${config.className}`}>
      {config.label}
    </span>
  );
};

const DossierDetailRes022 = () => {
  const { id } = useParams();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reextractFiles, setReextractFiles] = useState([]);
  const [reextracting, setReextracting] = useState(false);
  const [existingSources, setExistingSources] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [docStatus, setDocStatus] = useState({});
  const navigate = useNavigate();

  const fetchDocStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_DOCS}/doc-status/${id}`);
      setDocStatus(res.data.available_documents || {});
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dossierRes] = await Promise.all([
          axios.get(`${API}/dossiers-res022/${id}`),
        ]);
        setDossier(dossierRes.data);
        // Check actual document availability from S3/cache
        try {
          const docRes = await axios.get(`${API_DOCS}/doc-status/${id}`);
          setDocStatus(docRes.data.available_documents || {});
        } catch { /* ignore */ }
        // Check existing source files
        try {
          const srcRes = await axios.get(`${API}/extraction/sources/${id}`);
          setExistingSources(srcRes.data);
        } catch { /* ignore */ }
      } catch (error) {
        toast.error("Error al cargar dossier");
        navigate("/dossiers-res022");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleStatusChange = async (newStatus) => {
    try {
      try {
        await axios.put(`${API}/dossiers-res022/${id}`, { statut: newStatus });
      } catch (e) {
        const d = e.response?.data?.detail;
        if (e.response?.status !== 409 || !d?.confirmar) throw e;
        if (!window.confirm(`⚠ ${d.mensaje}`)) return;
        await axios.put(`${API}/dossiers-res022/${id}`, { statut: newStatus, confirmar: true });
      }
      setDossier({ ...dossier, statut: newStatus });
      toast.success("Estado actualizado");
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al actualizar estado");
    }
  };

  const [generatingDoc, setGeneratingDoc] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [signingAll, setSigningAll] = useState(false);
  const [sectionsKey, setSectionsKey] = useState(0);

  const SIGNABLE_DOCS = ["certificado_pdf", "fotos_pdf"];

  const handleSignAll = async () => {
    setSigningAll(true);
    try {
      for (const docType of SIGNABLE_DOCS) {
        await axios.post(`${API}/signature-res022/${id}/${docType}`);
      }
      toast.success("Certificado y Fotos firmados correctamente");
      setSectionsKey(k => k + 1);
      await fetchDocStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al firmar");
    } finally {
      setSigningAll(false);
    }
  };
  const [extractingDni, setExtractingDni] = useState(false);
  const [extractingXml, setExtractingXml] = useState(false);

  // Silent save - saves current form data to DB without navigation
  const silentSave = async () => {
    const donnees = JSON.parse(JSON.stringify(dossier.donnees));
    if (donnees.geolocalisation) {
      donnees.geolocalisation.huso = String(donnees.geolocalisation.huso || "");
      donnees.geolocalisation.utm_x = parseFloat(donnees.geolocalisation.utm_x) || 0;
      donnees.geolocalisation.utm_y = parseFloat(donnees.geolocalisation.utm_y) || 0;
    }
    if (donnees.batiment) {
      donnees.batiment.superficie_habitable = parseFloat(donnees.batiment.superficie_habitable) || 0;
      donnees.batiment.annee_construction = parseInt(donnees.batiment.annee_construction) || 0;
    }
    if (donnees.techniques) {
      for (const k of ["superficie_partition"]) {
        if (k in donnees.techniques) donnees.techniques[k] = parseFloat(donnees.techniques[k]) || 0;
      }
    }
    await axios.put(`${API}/dossiers-res022/${id}`, {
      donnees,
      notes: dossier.notes,
      date_installation: dossier.date_installation
    });
  };

  const pollGenerationStatus = async (taskId, toastId, labelPrefix) => {
    const maxPolls = 120; // 4 minutes max (120 * 2s)
    for (let i = 0; i < maxPolls; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await axios.get(`${API_DOCS}/generation-status/${taskId}?_t=${Date.now()}`);
        const { status, documents, completed, total, failed, errors } = res.data;

        if (status === "completed") {
          if (failed > 0) {
            const detail = errors && Object.values(errors)[0];
            toast.warning(
              `${labelPrefix}: ${completed}/${total} generados (${failed} errores)${detail ? ` — ${detail}` : ""}`,
              { id: toastId, duration: 8000 }
            );
          } else {
            toast.success(`${labelPrefix}: ${completed}/${total} generados`, { id: toastId });
          }
          return res.data;
        }
        if (status === "failed") {
          toast.error(`${labelPrefix}: Error de génération`, { id: toastId });
          return res.data;
        }
        // Still processing - show progress
        const generating = Object.entries(documents).find(([, s]) => s === "generating");
        const genName = generating ? generating[0].replace("_pdf", "").replace("_odt", "") : "";
        toast.loading(`${labelPrefix}: ${completed}/${total}${genName ? ` - ${genName}...` : ""}`, { id: toastId });
      } catch (err) {
        // Handle 404 (task lost after server restart) - stop polling
        if (err?.response?.status === 404) {
          toast.error(`${labelPrefix}: Tâche perdue (serveur redémarré). Relancez la génération.`, { id: toastId });
          return { status: "failed" };
        }
        // Other network error - retry silently
      }
    }
    toast.error(`${labelPrefix}: Timeout - vérifiez plus tard`, { id: toastId });
    return { status: "timeout" };
  };

  const handleGenerateAll = async () => {
    setGeneratingAll(true);
    const allTypes = pdfTypes.map(d => d.id);
    try {
      await silentSave();
      toast.loading("Generando todos los documentos...", { id: "gen-all" });
      const res = await axios.post(`${API_DOCS}/generate-documents`, {
        dossier_id: id,
        document_types: allTypes,
      });
      const taskId = res.data.task_id;
      const result = await pollGenerationStatus(taskId, "gen-all", "Documentos");
      toast.loading("Documentos: Justificante y Plantilla...", { id: "gen-all" });
      await axios.post(`${API}/justificante-res022/${id}/justificante/generate`);
      await axios.post(`${API}/justificante-res022/${id}/plantilla/generate`);
      setSectionsKey(k => k + 1);
      if (result.status === "completed" && !result.failed) toast.success(`Documentos: ${result.total + 2}/${result.total + 2} generados`, { id: "gen-all" });
      // pollGenerationStatus already set the final toast (success/warning with
      // per-doc error detail) -- don't overwrite it with a blanket success.
      const response = await axios.get(`${API}/dossiers-res022/${id}`);
      setDossier(response.data);
      await fetchDocStatus();
    } catch (error) {
      toast.error("Error al generar documentos", { id: "gen-all" });
    } finally {
      setGeneratingAll(false);
    }
  };

  const handleGenerateSingle = async (docType) => {
    setGeneratingDoc(docType);
    try {
      await silentSave();
      toast.loading("Generando...", { id: `gen-${docType}` });
      const res = await axios.post(`${API_DOCS}/generate-documents`, {
        dossier_id: id,
        document_types: [docType]
      });
      const taskId = res.data.task_id;
      // pollGenerationStatus already sets the final toast (success/warning with
      // per-doc error detail) -- don't overwrite it with a blanket success.
      await pollGenerationStatus(taskId, `gen-${docType}`, "Documento");
      setSectionsKey(k => k + 1);
      const response = await axios.get(`${API}/dossiers-res022/${id}`);
      setDossier(response.data);
      await fetchDocStatus();
    } catch (error) {
      toast.error("Error al generar documento", { id: `gen-${docType}` });
    } finally {
      setGeneratingDoc(null);
    }
  };

  const downloadingRef = useRef({});
  const handleDownload = async (docType) => {
    if (downloadingRef.current[docType]) return;
    downloadingRef.current[docType] = true;
    try {
      const res = await axios.get(`${API_DOCS}/download-document/${id}/${docType}`, { responseType: 'blob', validateStatus: (s) => s < 500 });
      if (res.status === 404 || res.status === 202) {
        downloadingRef.current[docType] = false;
        toast.error("Documento no generado. Haz clic en 'Generar' primero.", { id: `dl-${docType}` });
        return;
      }
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const disposition = res.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename=(.+)/);
      link.href = url;
      link.download = filenameMatch ? filenameMatch[1] : `${docType}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 200);
    } catch {
      toast.error("Error al descargar el documento");
    } finally {
      downloadingRef.current[docType] = false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const pdfTypes = [
    { id: "acuerdo_pdf", name: "Acuerdo Cesión", icon: FileText },
    { id: "certificado_pdf", name: "Certificado Obra", icon: FileText },
    { id: "anexo_pdf", name: "Anexo I", icon: FileText },
    { id: "fotos_pdf", name: "Fotos Actuación", icon: Camera },
    { id: "factura_pdf", name: "Factura", icon: FileText },
    { id: "dni_pdf", name: "DNI", icon: FileText },
  ];

  const wordTypes = [
    { id: "acuerdo_odt", name: "Acuerdo Cesión", icon: FileType },
    { id: "certificado_odt", name: "Certificado Obra", icon: FileType },
    { id: "fotos_odt", name: "Fotos Actuación", icon: FileType },
    { id: "factura_odt", name: "Factura", icon: FileType },
  ];

  const { letra, anio, sup, vauzVal, aes, techo, aeTotal, minEs, prixFacture, valorEconomico, beneficio, beneficioM2 } = calcularBeneficio(dossier);
  const eur = (n) => n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <>
      {/* Image Viewer Modal — outside animated div to avoid transform breaking fixed positioning */}
      {viewingImage && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setViewingImage(null)}
        >
          <div className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center">
            <div className="flex justify-between w-full mb-3">
              <p className="text-white text-sm">{viewingImage.title}</p>
              <button
                onClick={() => setViewingImage(null)}
                className="text-white hover:text-gray-300 text-xl font-bold"
              >
                ✕ Cerrar
              </button>
            </div>
            <img
              src={viewingImage.url}
              alt={viewingImage.title}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

    <div className="space-y-6 animate-fade-in-up" data-testid="dossier-detail">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              {dossier?.reference}
            </h1>
            <StatusBadge status={dossier?.statut} />
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800">
              RES022
            </span>
          </div>
          <p className="text-slate-500 mt-1">
            Fecha de instalación: {(() => {
              const d = dossier?.date_installation || dossier?.date_creation;
              if (!d) return "-";
              // Handle "dd/mm/yyyy" format
              if (/^\d{2}\/\d{2}\/\d{4}$/.test(d)) {
                const [day, month, year] = d.split('/');
                return new Date(year, month - 1, day).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
              }
              // Handle "yyyy-mm-dd" format
              if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
                const [year, month, day] = d.split('-');
                return new Date(year, month - 1, day).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
              }
              // Handle ISO format
              const parsed = new Date(d);
              return isNaN(parsed) ? d : parsed.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
            })()}
          </p>
          {dossier?.icerti_pedido?.pedido_id && (
            <p className="text-slate-500 mt-1">
              Pedido iCerti:{" "}
              <a
                href={`https://icerti.es/mi-cuenta/view-order/${dossier.icerti_pedido.pedido_id}/`}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline font-medium"
              >
                #{dossier.icerti_pedido.numero || dossier.icerti_pedido.pedido_id}
              </a>
              {dossier.icerti_pedido.expediente && ` · expediente ${dossier.icerti_pedido.expediente}`}
              {dossier.icerti_pedido.estado && ` · ${dossier.icerti_pedido.estado}`}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Select value={dossier?.statut} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-48" data-testid="status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nuevo">Nuevo</SelectItem>
              <SelectItem value="en_cours">En Curso</SelectItem>
              <SelectItem value="enviado_greenflex">Enviado Greenflex</SelectItem>
              <SelectItem value="enviado_moneleg">Enviado Moneleg</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => navigate(`/dossier-res022/${id}/edit`)}>
            <Edit className="w-4 h-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Identity Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Datos del Propietario
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-slate-500">Nombre</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.nom_proprietaire || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">DNI/NIE</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.dni || "-"}</dd>
                </div>
                <div className="md:col-span-2">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="extract-dni-btn"
                    disabled={extractingDni}
                    onClick={async () => {
                      setExtractingDni(true);
                      try {
                        const res = await axios.post(`${API}/extract-dni-res022/${id}`);
                        if (res.data.success) {
                          const updates = {};
                          if (res.data.nombre_completo) updates["donnees.identite.nom_proprietaire"] = res.data.nombre_completo;
                          if (res.data.dni) updates["donnees.identite.dni"] = res.data.dni;
                          if (Object.keys(updates).length > 0) {
                            const updatedDonnees = { ...dossier.donnees };
                            if (res.data.nombre_completo) updatedDonnees.identite = { ...updatedDonnees.identite, nom_proprietaire: res.data.nombre_completo };
                            if (res.data.dni) updatedDonnees.identite = { ...updatedDonnees.identite, dni: res.data.dni };
                            await axios.put(`${API}/dossiers-res022/${id}`, { donnees: updatedDonnees });
                            setDossier(prev => ({ ...prev, donnees: updatedDonnees }));
                            toast.success(`DNI extraído: ${res.data.nombre_completo || ""} - ${res.data.dni || ""}`);
                          } else {
                            toast.warning("No se pudo extraer datos del DNI");
                          }
                        }
                      } catch (err) {
                        const detail = err.response?.data?.detail || "";
                        if (detail.includes("foto")) {
                          toast.error("No hay foto de DNI. Sube primero el DNI recto.");
                        } else {
                          toast.error(detail || "Error al extraer DNI");
                        }
                      } finally {
                        setExtractingDni(false);
                      }
                    }}
                  >
                    {extractingDni ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5 mr-1.5" />}
                    Extraer DNI
                  </Button>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-slate-500">Dirección completa</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.adresse_complete || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Tipo de vía</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.type_voie || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Nombre de la vía</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.nom_voie || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Número</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.numero || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Escalera</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.escalier || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Planta</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.etage || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Puerta</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.porte || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Ciudad</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.ville || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Provincia</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.province || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Comunidad Autónoma</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.region || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Código Postal</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.identite?.code_postal || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Teléfono</dt>
                  <dd className="text-base text-slate-900" data-testid="propietario-telefono">{dossier?.donnees?.identite?.telephone || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Correo electrónico</dt>
                  <dd className="text-base text-slate-900" data-testid="propietario-email">{dossier?.donnees?.identite?.email || "-"}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* Building Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Datos del Edificio
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  data-testid="extract-xml-cee-btn"
                  disabled={extractingXml}
                  onClick={async () => {
                    setExtractingXml(true);
                    try {
                      const res = await axios.post(`${API}/extract-xml-res022/${id}`);
                      if (res.data.success) {
                        const updatedDonnees = { ...dossier.donnees };
                        updatedDonnees.batiment = {
                          ...updatedDonnees.batiment,
                          zone_climatique: res.data.zona_climatica || updatedDonnees.batiment?.zone_climatique || "",
                          annee_construction: res.data.anio_construccion || updatedDonnees.batiment?.annee_construction || 0,
                          superficie_habitable: res.data.superficie_habitable || updatedDonnees.batiment?.superficie_habitable || 0,
                          ref_catastrale: res.data.ref_catastral_xml || updatedDonnees.batiment?.ref_catastrale || "",
                          ref_catastrale_completa: res.data.ref_catastral_completa || updatedDonnees.batiment?.ref_catastrale_completa || "",
                        };
                        updatedDonnees.identite = { ...updatedDonnees.identite, adresse_complete: res.data.adresse_complete || updatedDonnees.identite?.adresse_complete || "" };
                        updatedDonnees.techniques = {
                          ...updatedDonnees.techniques,
                          superficie_partition: res.data.superficie_partition || updatedDonnees.techniques?.superficie_partition || 0,
                          cef_especifico: res.data.cef_especifico || updatedDonnees.techniques?.cef_especifico || 0,
                          fecha_cee_anterior: res.data.fecha_cee_anterior || updatedDonnees.techniques?.fecha_cee_anterior || "",
                          numero_registro_cee: res.data.numero_registro_cee || updatedDonnees.techniques?.numero_registro_cee || "",
                        };
                        await axios.put(`${API}/dossiers-res022/${id}`, { donnees: updatedDonnees });
                        setDossier(prev => ({ ...prev, donnees: updatedDonnees }));
                        if (res.data.pendientes?.length) toast.warning(`Pendiente: ${res.data.pendientes.join("; ")}`);
                        if (res.data.calculo) {
                          toast.success(`Extraído del XML — AE previsto: ${res.data.calculo.ae} kWh/año`);
                        } else {
                          toast.success("Datos extraídos del XML CEE");
                        }
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Error al extraer el XML CEE");
                    } finally {
                      setExtractingXml(false);
                    }
                  }}
                >
                  {extractingXml ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5 mr-1.5" />}
                  Extraer XML CEE
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-slate-500">Referencia Catastral</dt>
                  {dossier?.donnees?.batiment?.ref_catastrale ? (
                    <div className="flex items-center gap-3">
                      <a href={catastroUrl(dossier.donnees.batiment.ref_catastrale, API)}
                        target="_blank" rel="noreferrer"
                        className="text-base text-blue-600 hover:text-blue-800 hover:underline font-mono">
                        {dossier.donnees.batiment.ref_catastrale}
                      </a>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
                        try {
                          const ref = dossier.donnees.batiment.ref_catastrale;
                          const res = await axios.get(`${API}/cadastre-res022/full/${encodeURIComponent(ref)}`);
                          const d = res.data;
                          const guardado = await axios.put(`${API}/dossiers-res022/${dossier.id}/utm`, d);
                          setDossier(prev => ({
                            ...prev,
                            donnees: {
                              ...prev.donnees,
                              geolocalisation: { ...prev.donnees.geolocalisation, huso: d.huso, utm_x: d.utm_x, utm_y: d.utm_y },
                              identite: {
                                ...prev.donnees.identite,
                                adresse_complete: guardado.data.adresse_complete || prev.donnees.identite.adresse_complete,
                                ...(d.type_voie && { type_voie: d.type_voie }),
                                ...(d.nom_voie && { nom_voie: d.nom_voie }),
                                ...(d.numero && { numero: d.numero }),
                                ...(d.escalera && { escalier: d.escalera }),
                                ...(d.planta && { etage: d.planta }),
                                ...(d.puerta && { porte: d.puerta }),
                                ...(d.city && { ville: d.city }),
                                ...(d.province && { province: d.province }),
                                ...(d.region && { region: d.region }),
                                ...(d.postal_code && { code_postal: d.postal_code }),
                              },
                              batiment: {
                                ...prev.donnees.batiment,
                                ...(d.year && { annee_construction: d.year }),
                              }
                            }
                          }));
                          toast.success("Datos extraídos del Catastro");
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Error al consultar el Catastro");
                        }
                      }} data-testid="extract-cadastre-btn">
                        <RefreshCw size={12} className="mr-1" /> Extraer Cadastro
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
                        try {
                          const ref = dossier.donnees.batiment.ref_catastrale;
                          const res = await axios.get(`${API}/cadastre-res022/navarra/${encodeURIComponent(ref)}`);
                          const d = res.data;
                          const guardado = await axios.put(`${API}/dossiers-res022/${dossier.id}/utm`, d);
                          setDossier(prev => ({
                            ...prev,
                            donnees: {
                              ...prev.donnees,
                              geolocalisation: { ...prev.donnees.geolocalisation, huso: d.huso, utm_x: d.utm_x, utm_y: d.utm_y },
                              identite: {
                                ...prev.donnees.identite,
                                adresse_complete: guardado.data.adresse_complete || prev.donnees.identite.adresse_complete,
                                ...(d.type_voie && { type_voie: d.type_voie }),
                                ...(d.nom_voie && { nom_voie: d.nom_voie }),
                                ...(d.numero && { numero: d.numero }),
                                ...(d.escalera && { escalier: d.escalera }),
                                ...(d.planta && { etage: d.planta }),
                                ...(d.puerta && { porte: d.puerta }),
                                ...(d.city && { ville: d.city }),
                                ...(d.province && { province: d.province }),
                                ...(d.region && { region: d.region }),
                                ...(d.postal_code && { code_postal: d.postal_code }),
                              },
                            }
                          }));
                          toast.success("Datos extraídos del Catastro de Navarra");
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Error al consultar el Catastro de Navarra");
                        }
                      }} data-testid="extract-cadastre-navarra-btn">
                        <RefreshCw size={12} className="mr-1" /> Extraer Cadastro (Navarra)
                      </Button>
                    </div>
                  ) : (
                    <dd className="text-base text-slate-900 font-mono">-</dd>
                  )}
                  {dossier?.donnees?.batiment?.ref_catastrale_completa && dossier.donnees.batiment.ref_catastrale_completa !== dossier.donnees.batiment.ref_catastrale && (
                    <p className="text-sm text-slate-500 mt-1" data-testid="ref-catastral-completa">
                      Completa (documentos): <span className="font-mono text-slate-900">{dossier.donnees.batiment.ref_catastrale_completa}</span>
                    </p>
                  )}
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Superficie Habitable</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.batiment?.superficie_habitable || 0} m²</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Zona Climática</dt>
                  <dd className="text-base text-slate-900">{dossier?.donnees?.batiment?.zone_climatique || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Geolocalización UTM</dt>
                  <dd className="text-base text-slate-900 mt-1" data-testid="detail-utm-edificio">
                    Huso: {dossier?.donnees?.geolocalisation?.huso || "-"} ETRS89 X = {Math.trunc(dossier?.donnees?.geolocalisation?.utm_x || 0)} Y = {Math.trunc(dossier?.donnees?.geolocalisation?.utm_y || 0)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Superficie Partición (isolée)</dt>
                  <dd className="text-base text-slate-900">{sup} m²</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Año de Construcción</dt>
                  <dd className="text-base text-slate-900">{anio || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Nº Registro CEE / Justificante</dt>
                  {/^\d{2}\/\d+\.\d\/\d{2}$/.test(dossier?.donnees?.techniques?.numero_registro_cee || "") ? (
                    <dd>
                      <button type="button" className="text-base text-blue-600 hover:underline font-mono" title="Copia el número y abre el buscador de etiquetas de la Comunidad de Madrid"
                        onClick={() => navigator.clipboard.writeText(dossier.donnees.techniques.numero_registro_cee).then(() => toast.success("Número copiado")).catch(() => toast.error("No se pudo copiar")).finally(() => window.open("https://gestiona.comunidad.madrid/reee_etiqueta/showBuscadorEtiqueta.jsf", "_blank", "noopener"))}>
                        {dossier.donnees.techniques.numero_registro_cee}
                      </button>
                    </dd>
                  ) : (
                    <dd className="text-base text-slate-900">{dossier?.donnees?.techniques?.numero_registro_cee || "-"}</dd>
                  )}
                </div>
              </dl>
              <Separator className="my-4" />

              {/* Calculated mapping values for documents -- same AES/CEF/techo
                  formula as the Certificado Obra RES022 (res022_certificado_calc.py),
                  replacing the old (Ui-Uf)*S*G estimate. */}
                  <div className="bg-blue-50 rounded-lg p-4 space-y-3" data-testid="mapping-calculs">
                    <h4 className="text-sm font-semibold text-blue-800">Valores del Acuerdo de Cesión</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <dt className="text-xs font-medium text-blue-600">VAUZ (Zona {letra || '-'})</dt>
                        <dd className="text-lg font-bold text-slate-900" data-testid="mapping-g">{vauzVal}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-blue-600">AE Total (kWh)</dt>
                        <dd className="text-lg font-bold text-slate-900" data-testid="mapping-ae-total">{aeTotal.toLocaleString('es-ES')}</dd>
                        <p className="text-xs text-slate-400">mín(AES={Math.round(aes)} ; Techo CEF={redondeoAE(techo)}) = {minEs}</p>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-blue-600">Precio Factura (HT)</dt>
                        <dd className="text-lg font-bold text-slate-900" data-testid="mapping-prix-facture">{prixFacture.toLocaleString('es-ES')}€</dd>
                        <p className="text-xs text-slate-400">= 8 x {sup} m²</p>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-blue-600">Valor Económico</dt>
                        <dd className="text-lg font-bold text-green-700" data-testid="mapping-valor-economico">{(Math.floor(valorEconomico * 10000000) / 10000000).toFixed(7).replace('.', ',')}€/kWh</dd>
                        <p className="text-xs text-slate-400">= {prixFacture} / {aeTotal}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 space-y-3 mt-4" data-testid="beneficio-dossier">
                    <h4 className="text-sm font-semibold text-blue-800">Beneficio</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <dt className="text-xs font-medium text-blue-600">Beneficio por m²</dt>
                        <dd className="text-lg font-bold text-blue-700">{eur(beneficioM2)} €/m²</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-blue-600">Total dossier</dt>
                        <dd className="text-lg font-bold text-blue-700">{eur(beneficio)} €</dd>
                        <p className="text-xs text-slate-400">= {aeTotal.toLocaleString('es-ES')} kWh × 0,156 €/kWh</p>
                      </div>
                    </div>
                  </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Upload Fotos Antes/Después */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Fotos Antes / Después / Vivienda / Extra
              </CardTitle>
              <CardDescription>
                Sube 7 fotos antes, 7 fotos después, 2 fotos de la vivienda y 3 opcionales
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase">Antes (7)</p>
              {[1, 2, 3, 4, 5, 6, 7].map((position) => {
                const hasFoto = dossier?.fotos_avant_apres?.[position - 1];
                return (
                  <div key={`foto-antes-${position}`} className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs font-bold text-amber-700">
                      {position}
                    </span>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`foto-${position}`}
                        data-testid={`foto-antes-${position}`}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          toast.loading(`Subiendo foto ${position}...`, { id: `foto-${position}` });
                          try {
                            await axios.post(`${API}/upload-foto-res022/${id}/${position}`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            toast.success(`Foto ${position} subida`, { id: `foto-${position}` });
                            const response = await axios.get(`${API}/dossiers-res022/${id}`);
                            setDossier(response.data);
                          } catch (error) {
                            console.error("Upload foto avant error:", error);
                            toast.error(`Error al subir foto ${position}`, { id: `foto-${position}` });
                          }
                        }}
                      />
                      {hasFoto ? (
                        <div className="flex items-center justify-between w-full p-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <span className="text-amber-700 text-sm font-medium">✓ Foto {position}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setViewingImage({ url: `${API}/dossier-file-res022/${id}/foto/${position}?t=${Date.now()}`, title: `Foto Antes ${position}` })} className="p-1.5 text-slate-600 hover:bg-white rounded" title="Ver">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button onClick={async () => { if (!confirm('¿Eliminar?')) return; try { await axios.delete(`${API}/dossier-file-res022/${id}/foto/${position}`); toast.success('Eliminada'); const r = await axios.get(`${API}/dossiers-res022/${id}`); setDossier(r.data); } catch { toast.error('Error'); }}} className="p-1.5 text-red-500 hover:bg-white rounded" title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label
                          htmlFor={`foto-${position}`}
                          className="flex items-center justify-center gap-2 w-full p-2 text-sm rounded-lg border border-dashed border-slate-300 hover:border-amber-400 hover:bg-amber-50/50 cursor-pointer transition-colors text-slate-400 hover:text-amber-600"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          Foto {position}
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
              <p className="text-xs font-semibold text-slate-500 uppercase mt-3">Después (7)</p>
              {[8, 9, 10, 11, 12, 13, 14].map((position) => {
                const hasFoto = dossier?.fotos_avant_apres?.[position - 1];
                return (
                  <div key={`foto-despues-${position}`} className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-xs font-bold text-emerald-700">
                      {position - 7}
                    </span>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`foto-${position}`}
                        data-testid={`foto-despues-${position}`}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          toast.loading(`Subiendo foto ${position}...`, { id: `foto-${position}` });
                          try {
                            await axios.post(`${API}/upload-foto-res022/${id}/${position}`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            toast.success(`Foto ${position} subida`, { id: `foto-${position}` });
                            const response = await axios.get(`${API}/dossiers-res022/${id}`);
                            setDossier(response.data);
                          } catch (error) {
                            console.error("Upload foto apres error:", error);
                            toast.error(`Error al subir foto ${position}`, { id: `foto-${position}` });
                          }
                        }}
                      />
                      {hasFoto ? (
                        <div className="flex items-center justify-between w-full p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <span className="text-emerald-700 text-sm font-medium">✓ Foto {position - 7}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setViewingImage({ url: `${API}/dossier-file-res022/${id}/foto/${position}?t=${Date.now()}`, title: `Foto Después ${position - 7}` })} className="p-1.5 text-slate-600 hover:bg-white rounded" title="Ver">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button onClick={async () => { if (!confirm('¿Eliminar?')) return; try { await axios.delete(`${API}/dossier-file-res022/${id}/foto/${position}`); toast.success('Eliminada'); const r = await axios.get(`${API}/dossiers-res022/${id}`); setDossier(r.data); } catch { toast.error('Error'); }}} className="p-1.5 text-red-500 hover:bg-white rounded" title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label
                          htmlFor={`foto-${position}`}
                          className="flex items-center justify-center gap-2 w-full p-2 text-sm rounded-lg border border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/50 cursor-pointer transition-colors text-slate-400 hover:text-emerald-600"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          Foto {position - 7}
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
              <p className="text-xs font-semibold text-slate-500 uppercase mt-3">Fotos Vivienda (2)</p>
              {[15, 16].map((position) => {
                const hasFoto = dossier?.fotos_avant_apres?.[position - 1];
                return (
                  <div key={`foto-vivienda-${position}`} className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-700">
                      {position - 14}
                    </span>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`foto-${position}`}
                        data-testid={`foto-vivienda-${position}`}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          toast.loading(`Subiendo foto vivienda ${position - 14}...`, { id: `foto-${position}` });
                          try {
                            await axios.post(`${API}/upload-foto-res022/${id}/${position}`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            toast.success(`Foto vivienda ${position - 14} subida`, { id: `foto-${position}` });
                            const response = await axios.get(`${API}/dossiers-res022/${id}`);
                            setDossier(response.data);
                          } catch (error) {
                            console.error("Upload foto vivienda error:", error);
                            toast.error(`Error al subir foto vivienda`, { id: `foto-${position}` });
                          }
                        }}
                      />
                      {hasFoto ? (
                        <div className="flex items-center justify-between w-full p-2 bg-blue-50 border border-blue-200 rounded-lg">
                          <span className="text-blue-700 text-sm font-medium">✓ Foto {position - 14}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setViewingImage({ url: `${API}/dossier-file-res022/${id}/foto/${position}?t=${Date.now()}`, title: `Foto Vivienda ${position - 14}` })} className="p-1.5 text-slate-600 hover:bg-white rounded" title="Ver">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button onClick={async () => { if (!confirm('¿Eliminar?')) return; try { await axios.delete(`${API}/dossier-file-res022/${id}/foto/${position}`); toast.success('Eliminada'); const r = await axios.get(`${API}/dossiers-res022/${id}`); setDossier(r.data); } catch { toast.error('Error'); }}} className="p-1.5 text-red-500 hover:bg-white rounded" title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label
                          htmlFor={`foto-${position}`}
                          className="flex items-center justify-center gap-2 w-full p-2 text-sm rounded-lg border border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer transition-colors text-slate-400 hover:text-blue-600"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          Foto {position - 14}
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
              <p className="text-xs font-semibold text-slate-500 uppercase mt-3">Fotos Extra - Opcional (3)</p>
              {[17, 18, 19].map((position) => {
                const hasFoto = dossier?.fotos_avant_apres?.[position - 1];
                return (
                  <div key={`foto-extra-${position}`} className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-xs font-bold text-purple-700">
                      {position - 16}
                    </span>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`foto-${position}`}
                        data-testid={`foto-extra-${position}`}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          toast.loading(`Subiendo foto extra ${position - 16}...`, { id: `foto-${position}` });
                          try {
                            await axios.post(`${API}/upload-foto-res022/${id}/${position}`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            toast.success(`Foto extra ${position - 16} subida`, { id: `foto-${position}` });
                            const response = await axios.get(`${API}/dossiers-res022/${id}`);
                            setDossier(response.data);
                          } catch (error) {
                            console.error("Upload foto extra error:", error);
                            toast.error(`Error al subir foto extra`, { id: `foto-${position}` });
                          }
                        }}
                      />
                      {hasFoto ? (
                        <div className="flex items-center justify-between w-full p-2 bg-purple-50 border border-purple-200 rounded-lg">
                          <span className="text-purple-700 text-sm font-medium">✓ Foto {position - 16}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setViewingImage({ url: `${API}/dossier-file-res022/${id}/foto/${position}?t=${Date.now()}`, title: `Foto Extra ${position - 16}` })} className="p-1.5 text-slate-600 hover:bg-white rounded" title="Ver">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button onClick={async () => { if (!confirm('¿Eliminar?')) return; try { await axios.delete(`${API}/dossier-file-res022/${id}/foto/${position}`); toast.success('Eliminada'); const r = await axios.get(`${API}/dossiers-res022/${id}`); setDossier(r.data); } catch { toast.error('Error'); }}} className="p-1.5 text-red-500 hover:bg-white rounded" title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label
                          htmlFor={`foto-${position}`}
                          className="flex items-center justify-center gap-2 w-full p-2 text-sm rounded-lg border border-dashed border-slate-300 hover:border-purple-400 hover:bg-purple-50/50 cursor-pointer transition-colors text-slate-400 hover:text-purple-600"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          Foto {position - 16} (opcional)
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
              <p className="text-xs font-semibold text-slate-500 uppercase mt-3">Fotos DNI (2)</p>
              {[
                { position: 1, label: "DNI Recto" },
                { position: 2, label: "DNI Verso" }
              ].map(({ position, label }) => {
                const hasDni = dossier?.dni_images?.[position - 1] && typeof dossier.dni_images[position - 1] === 'string' && dossier.dni_images[position - 1] !== '';
                return (
                  <div key={`dni-${position}`} className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center text-xs font-bold text-sky-700">
                      {position}
                    </span>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`dni-${position}`}
                        data-testid={`dni-upload-${position}`}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          toast.loading(`Subiendo ${label}...`, { id: `dni-${position}` });
                          try {
                            await axios.post(`${API}/upload-dni-res022/${id}/${position}`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            toast.success(`${label} subido`, { id: `dni-${position}` });
                            const response = await axios.get(`${API}/dossiers-res022/${id}`);
                            setDossier(response.data);
                          } catch (error) {
                            console.error("Upload DNI error:", error);
                            toast.error(`Error al subir ${label}`, { id: `dni-${position}` });
                          }
                        }}
                      />
                      {hasDni ? (
                        <div className="flex items-center justify-between w-full p-2 bg-sky-50 border border-sky-200 rounded-lg">
                          <span className="text-sky-700 text-sm font-medium">✓ {label}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setViewingImage({ url: `${API}/dossier-file-res022/${id}/dni/${position}?t=${Date.now()}`, title: label })} className="p-1.5 text-slate-600 hover:bg-white rounded" title="Ver">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button onClick={async () => { if (!confirm('¿Eliminar?')) return; try { await axios.delete(`${API}/dossier-file-res022/${id}/dni/${position}`); toast.success('Eliminado'); const r = await axios.get(`${API}/dossiers-res022/${id}`); setDossier(r.data); } catch { toast.error('Error'); }}} className="p-1.5 text-red-500 hover:bg-white rounded" title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label
                          htmlFor={`dni-${position}`}
                          className="flex items-center justify-center gap-2 w-full p-2 text-sm rounded-lg border border-dashed border-slate-300 hover:border-sky-400 hover:bg-sky-50/50 cursor-pointer transition-colors text-slate-400 hover:text-sky-600"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          {label}
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-slate-400 mt-2">
                Las fotos se insertarán en el documento Fotos Actuación
              </p>
            </CardContent>
          </Card>

          {/* Documentos Adicionales */}
          <DocumentosAdicionales key={sectionsKey} dossierId={id} />
          <EtiquetaNavarra dossierId={id} refCatastral={dossier?.donnees?.batiment?.ref_catastrale} onDone={() => setSectionsKey(k => k + 1)} />

          {/* Firmas del cliente */}
          {dossier?.appointment_id && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Pen className="w-5 h-5 text-emerald-600" />
                  Firmas del Cliente
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dossier.tech_captures && (dossier.tech_captures.acuerdo_signature || dossier.tech_captures.anexo_signature || dossier.tech_captures.justificante_signature || dossier.tech_captures.plantilla_signature) ? (
                  <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      ["acuerdo_signature", "Firma Acuerdo"],
                      ["acuerdo_nombre", "Nombre Acuerdo"],
                      ["acuerdo_dni_handwritten", "DNI Acuerdo"],
                      ["anexo_signature", "Firma Anexo"],
                      ["anexo_nombre", "Nombre Anexo"],
                      ["anexo_dni_handwritten", "DNI Anexo"],
                      ["justificante_signature", "Firma Justificante"],
                      ["plantilla_signature", "Firma Plantilla CAE"],
                    ].map(([key, label]) => 
                      dossier.tech_captures[key] ? (
                        <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                          <div className="flex items-center gap-2">
                            <Pen className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm font-medium text-slate-700">{label}</span>
                          </div>
                          <button
                            data-testid={`view-${key}`}
                            onClick={() => setViewingImage({ url: dossier.tech_captures[key], title: label })}
                            className="p-1.5 text-slate-600 hover:bg-white rounded"
                            title="Ver"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      ) : null
                    )}
                  </div>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full"
                    data-testid="load-signatures-btn"
                    onClick={async () => {
                      try {
                        toast.loading("Cargando firmas...", { id: "load-tech" });
                        const techRes = await axios.get(`${API}/tech/appointments/${dossier.appointment_id}/data`);
                        if (techRes.data?.captures && Object.keys(techRes.data.captures).length > 0) {
                          await axios.put(`${API}/dossiers-res022/${id}`, { tech_captures: techRes.data.captures });
                          const response = await axios.get(`${API}/dossiers-res022/${id}`);
                          setDossier(response.data);
                          toast.success("Firmas cargadas", { id: "load-tech" });
                        } else {
                          toast.error("No hay firmas del técnico", { id: "load-tech" });
                        }
                      } catch (error) {
                        toast.error("Error al cargar firmas", { id: "load-tech" });
                      }
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Cargar firmas del técnico
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Factura */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Factura
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500">Nº Factura</Label>
                <div className="flex gap-2">
                  <Input
                    value={dossier?.num_facture || ""}
                    placeholder="Auto"
                    data-testid="num-facture-input"
                    onChange={(e) => setDossier({...dossier, num_facture: e.target.value})}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="save-num-facture"
                    onClick={async () => {
                      try {
                        let num = dossier?.num_facture;
                        if (!num) {
                          const res = await axios.get(`${API_DOCS}/next-invoice-number`);
                          num = res.data.next_number;
                        }
                        await axios.put(`${API_DOCS}/dossiers/${id}/num-facture`, { num_facture: num });
                        setDossier({...dossier, num_facture: num});
                        toast.success(`Nº factura: ${num}`);
                      } catch (error) {
                        toast.error("Error al guardar el número");
                      }
                    }}
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">Deja vacío para auto-numerar (desde 239)</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  data-testid="download-factura-word"
                  onClick={async () => {
                    try {
                      if (!dossier?.num_facture) {
                        const res = await axios.get(`${API_DOCS}/next-invoice-number`);
                        await axios.put(`${API_DOCS}/dossiers/${id}/num-facture`, { num_facture: res.data.next_number });
                        setDossier({...dossier, num_facture: res.data.next_number});
                      }
                      handleDownload('factura_odt');
                    } catch (error) {
                      toast.error("Error al descargar la factura Word");
                    }
                  }}
                >
                  <FileType className="w-4 h-4 mr-2" />
                  Word
                </Button>
                <Button
                  className="flex-1"
                  data-testid="download-factura-pdf"
                  onClick={async () => {
                    try {
                      if (!dossier?.num_facture) {
                        const res = await axios.get(`${API_DOCS}/next-invoice-number`);
                        await axios.put(`${API_DOCS}/dossiers/${id}/num-facture`, { num_facture: res.data.next_number });
                        setDossier({...dossier, num_facture: res.data.next_number});
                      }
                      handleDownload('factura_pdf');
                    } catch (error) {
                      toast.error("Error al descargar la factura PDF");
                    }
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Documentos */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Documentos</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSignAll}
                    disabled={signingAll}
                    className="text-purple-600 border-purple-300 hover:bg-purple-50 gap-1.5"
                    data-testid="sign-all-docs"
                    title="Generar y firmar Certificado + Fotos con certificado"
                  >
                    {signingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pen className="w-3.5 h-3.5" />}
                    Firmar Certificado + Fotos
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleGenerateAll}
                    disabled={generatingAll || !!generatingDoc}
                    className="bg-green-600 hover:bg-green-700 text-white gap-1.5"
                    data-testid="generate-all-docs"
                  >
                    {generatingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Generar todos
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2" data-version="2">
              <ControlDossierRes022 dossierId={id} refreshKey={sectionsKey} />
              {pdfTypes.map((doc) => {
                const isGenerated = !!docStatus[doc.id];
                const isSigned = SIGNABLE_DOCS.includes(doc.id) && !!docStatus[doc.id]?.firmado_en_crm;
                return (
                  <div key={doc.id} className={`flex items-center gap-2 p-2 rounded-lg border ${isSigned ? "bg-purple-50 border-purple-300" : "bg-slate-50"}`} data-testid={`doc-row-${doc.id}`}>
                    <doc.icon className={`w-4 h-4 shrink-0 ${isSigned ? "text-purple-600" : "text-slate-500"}`} />
                    <span className="text-sm font-medium text-slate-700 flex-1 min-w-0 truncate">
                      {doc.name}
                      {SIGNABLE_DOCS.includes(doc.id) && (
                        <span className={`ml-2 text-xs font-normal ${isSigned ? "text-purple-600" : "text-slate-400"}`}>
                          {isSigned ? "· Firmado" : isGenerated ? "· No firmado" : ""}
                        </span>
                      )}
                    </span>
                    {isGenerated && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(doc.id)}
                        data-testid={`download-${doc.id}`}
                        title="Descargar PDF"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant={isGenerated ? "ghost" : "default"}
                      size="sm"
                      disabled={generatingDoc === doc.id}
                      onClick={() => handleGenerateSingle(doc.id)}
                      data-testid={`generate-${doc.id}`}
                      title={isGenerated ? "Re-generar" : "Generar"}
                    >
                      {generatingDoc === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </Button>
                  </div>
                );
              })}
              <JustificanteSection key={sectionsKey} dossierId={id} />

              {/* Word Documents Section */}
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Documentos Word</p>
                {wordTypes.map((doc) => {
                  const odt_id = doc.id;
                  const isGenerated = !!docStatus[odt_id];
                  return (
                    <div key={doc.id} className="flex items-center gap-2 p-2 rounded-lg border bg-white mb-1">
                      <doc.icon className="w-4 h-4 text-blue-500 shrink-0" />
                      <span className="text-sm text-slate-600 flex-1 min-w-0 truncate">{doc.name}</span>
                      {isGenerated ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(odt_id)}
                          data-testid={`download-${odt_id}`}
                          title="Descargar Word"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={generatingDoc === odt_id}
                          onClick={() => handleGenerateSingle(odt_id)}
                          data-testid={`generate-${odt_id}`}
                          title="Generar Word"
                        >
                          {generatingDoc === odt_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Notas */}
          {dossier?.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  📝 Notas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{dossier.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Greenflex (eCAEs) - tout en bas */}
          <GreenflexSectionRes022 dossierId={id} dossier={dossier} />

          <EnviarDocumentosSection dossierId={id} />

        </div>
      </div>
    </div>

    </>
  );
};

export default DossierDetailRes022;
